import { useEffect, useRef, useState } from "react";
import alarmSound from "../assets/alarm.mp3";
import pizzaSound from "../assets/pizza.mp3";
import { Button, InputAdornment, styled, TextField } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../hooks/reduxHooks";
import { stopAlarm } from "../store/layout";
import { updateCurrentAlarmEnabled, updateCurrentAlarmThreshold } from "../store/settings";
import IconButton from "@mui/material/IconButton";
import { NotificationsActive, NotificationsOff, Percent } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { getAudio } from "../helpers/idb"; // Import getAudio from db.js

const WhiteStyledTextField = styled(TextField)(({ theme }) => ({
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "rgb(150, 150, 150)",
		},
		"&:hover fieldset": {
			borderColor: "rgb(160, 160, 160)",
		},
		"&.Mui-focused fieldset": {
			borderColor: "white",
		},
	},

	"& input[type=number]::-webkit-inner-spin-button": {
		"-webkit-appearance": "none",
		margin: 0,
	},

	"& .MuiInputBase-input": {
		color: "white",
	},
	"& .MuiInputLabel-root": {
		color: "white",
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: "white",
	},
	"& .MuiInputAdornment-root": {
		color: "white",
	},
}));

const Alarm = () => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const playAlarm = useAppSelector((state) => state.layout.playAlarm);
	const currentAlarmEnabled = useAppSelector((state) => state.settings.currentAlarmEnabled);
	const currentAlarmThreshold = useAppSelector((state) => state.settings.currentAlarmThreshold);
	const defaultAlarmSound = useAppSelector((state) => state.settings.defaultAlarmSound);
	const [showAlarmStatus, setShowAlarmStatus] = useState(false);
	const [tempThreshold, setTempThreshold] = useState<number | string>(currentAlarmThreshold || "");
	const dispatch = useAppDispatch();
	const location = useLocation();
	const [currentAlarmSound, setCurrentAlarmSound] = useState<string>(alarmSound);

	useEffect(() => {
		if (defaultAlarmSound === "pizza") {
			setCurrentAlarmSound(pizzaSound);
		} else {
			setCurrentAlarmSound(alarmSound);
		}
	}, [defaultAlarmSound]);

	useEffect(() => {
		// Update tempThreshold when currentAlarmThreshold changes
		setTempThreshold(currentAlarmThreshold || "");
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
			}
		};
	}, [currentAlarmThreshold]);

	useEffect(() => {
		if (location.pathname.startsWith("/counter/")) {
			setShowAlarmStatus(true);
			return;
		}
		setShowAlarmStatus(false);
	}, [location.pathname]);

	useEffect(() => {
		let audioUrl: string | null = null;

		const setupAudio = async () => {
			try {
				// Check for uploaded file in IndexedDB
				const uploadedAudio = await getAudio();
				if (uploadedAudio) {
					// Use uploaded file if available
					audioUrl = URL.createObjectURL(uploadedAudio);
				} else {
					// Fallback to default alarm.mp3
					audioUrl = currentAlarmSound;
				}

				// Create new Audio instance
				audioRef.current = new Audio(audioUrl);
			} catch (error) {
				console.error("Failed to load audio from IndexedDB:", error);
				// Fallback to default if IndexedDB fails
				audioRef.current = new Audio(currentAlarmSound);
			}

			const playAudio = async () => {
				if (audioRef.current && playAlarm) {
					try {
						await audioRef.current.play();
						audioRef.current.loop = true;
						console.log("Audio played successfully");
					} catch (error) {
						console.error("Failed to play audio:", error);
						alert("Please interact with the page to enable audio playback.");
					}
				}
			};

			if (playAlarm) {
				playAudio();
			}
		};

		setupAudio();

		// Cleanup
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
			}
			if (audioUrl && audioUrl !== currentAlarmSound) {
				URL.revokeObjectURL(audioUrl); // Clean up IndexedDB URL
			}
		};
	}, [currentAlarmSound, playAlarm]);

	const handleConfirmThreshold = () => {
		const value = Number(tempThreshold);
		if (isNaN(value)) {
			dispatch(updateCurrentAlarmThreshold(0));
		} else {
			const intValue = Math.floor(value);
			if (intValue < 0) {
				dispatch(updateCurrentAlarmThreshold(0));
			} else if (intValue > 1000) {
				dispatch(updateCurrentAlarmThreshold(1000));
			} else {
				dispatch(updateCurrentAlarmThreshold(intValue));
			}
		}
	};

	if (!showAlarmStatus) return null;

	return playAlarm ? (
		<Button variant="contained" onClick={() => dispatch(stopAlarm())} color="error">
			X
		</Button>
	) : (
		<>
			<IconButton color="inherit" onClick={() => dispatch(updateCurrentAlarmEnabled(!currentAlarmEnabled))}>
				{currentAlarmEnabled ? <NotificationsActive /> : <NotificationsOff />}
			</IconButton>
			{currentAlarmEnabled && (
				<WhiteStyledTextField
					size="small"
					type="number"
					variant="outlined"
					value={tempThreshold}
					onChange={(event) => {
						const value = event.target.value;
						setTempThreshold(value === "" ? "" : Number(value));
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							handleConfirmThreshold();
							(event.target as HTMLInputElement).blur();
						}
					}}
					inputProps={{
						min: 0,
						max: 1000,
						step: 1,
					}}
					InputLabelProps={{
						shrink: true,
					}}
					InputProps={{
						style: { padding: "0px", paddingRight: 0 },
						startAdornment: (
							<InputAdornment position="start" sx={{ paddingLeft: "4px" }}>
								<Percent sx={{ width: "16px" }} />
							</InputAdornment>
						),
						endAdornment: (
							<>
								{tempThreshold !== currentAlarmThreshold && (
									<Button color={"secondary"} variant="contained" onClick={handleConfirmThreshold} sx={{ color: "white" }}>
										Set
									</Button>
								)}
							</>
						),
					}}
				/>
			)}
		</>
	);
};

export default Alarm;
